import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  inviteStatus,
  type AuditEntry,
  type House,
  type Invite,
  type InviteStatus,
} from '@guest/shared';
import { ApiError, adminApi } from '../lib/api.js';
import { Icon } from '../lib/icons.js';
import { Badge, Panel, Toast } from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';
import { InviteEditor, type EditorValue } from './InviteEditor.js';
import { HouseInfoEditor } from './HouseInfoEditor.js';

const STATUS_TONE: Record<InviteStatus, 'positive' | 'caution' | 'alert' | undefined> = {
  active: 'positive',
  scheduled: 'caution',
  expired: undefined,
  revoked: 'alert',
};

type View =
  | { name: 'list' }
  | { name: 'new' }
  | { name: 'detail'; id: string }
  | { name: 'edit'; id: string }
  | { name: 'audit' }
  | { name: 'info' };

function Login({ onIn }: { onIn: () => void }): ReactNode {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.login(password);
      onIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="codepad">
      <form className="glass codepad-inner" onSubmit={(e) => void submit(e)}>
        <span className="hero-glyph" style={{ margin: '0 auto var(--space-4)' }}>
          <Icon name="lock" size={22} />
        </span>
        <h1>Manage access</h1>
        <p>Sign in to create and revoke guest codes.</p>
        <input
          type="password"
          className="field"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          autoComplete="current-password"
          aria-label="Admin password"
        />
        {error && (
          <p className="code-error" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="btn btn-primary btn-block"
          style={{ marginTop: 'var(--space-4)' }}
          disabled={busy || password.length === 0}
        >
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}

function InviteRow({ invite, onOpen }: { invite: Invite; onOpen: () => void }): ReactNode {
  const status = inviteStatus(invite);
  return (
    <button type="button" className="glass invite-row" onClick={onOpen}>
      <span className="invite-code">{invite.code}</span>
      <span className="invite-meta">
        <span className="who">{invite.guestName}</span>
        <span className="when">
          {formatDateTime(invite.startsAt)} → {formatDateTime(invite.endsAt)}
        </span>
      </span>
      <Badge {...(STATUS_TONE[status] ? { tone: STATUS_TONE[status] } : {})}>{status}</Badge>
    </button>
  );
}

function InviteDetail({
  invite,
  house,
  audit,
  onChanged,
  onEdit,
  onBack,
}: {
  invite: Invite;
  house: House;
  audit: AuditEntry[];
  onChanged: () => void;
  onEdit: () => void;
  onBack: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const status = inviteStatus(invite);

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const roomNames = invite.permissions.rooms
    .map((g) => {
      const room = house.rooms.find((r) => r.id === g.roomId);
      return room ? `${room.name} (${g.groups.length})` : g.roomId;
    })
    .join(', ');

  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <div className="admin-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back">
          <Icon name="back" size={17} />
        </button>
        <h1>{invite.guestName}</h1>
        <span className="spacer" />
        <Badge {...(STATUS_TONE[status] ? { tone: STATUS_TONE[status] } : {})}>{status}</Badge>
      </div>

      <Panel className="code-display">
        <div className="eyebrow">Access code</div>
        <div className="big">{invite.code}</div>
        <div className="chips" style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className="chip"
            onClick={() => {
              void navigator.clipboard?.writeText(invite.code);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1800);
            }}
          >
            <Icon name={copied ? 'check' : 'share'} size={15} />
            {copied ? 'Copied' : 'Copy code'}
          </button>
          <button
            type="button"
            className="chip"
            disabled={busy}
            onClick={() => void run(() => adminApi.regenerate(invite.id))}
          >
            <Icon name="key" size={15} />
            New code
          </button>
        </div>
      </Panel>

      <Panel title="Access" icon="clock" className="stack">
        <dl style={{ margin: 0, display: 'grid', gap: 8, fontSize: 14 }}>
          <div className="row" style={{ gap: 8 }}>
            <span style={{ color: 'var(--text-faint)', minWidth: 92 }}>Window</span>
            <span>
              {formatDateTime(invite.startsAt)} → {formatDateTime(invite.endsAt)}
            </span>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--text-faint)', minWidth: 92 }}>Rooms</span>
            <span style={{ color: 'var(--text-dim)' }}>{roomNames || 'None'}</span>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--text-faint)', minWidth: 92 }}>House-wide</span>
            <span style={{ color: 'var(--text-dim)' }}>
              {invite.permissions.globals.join(', ') || 'None'}
            </span>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <span style={{ color: 'var(--text-faint)', minWidth: 92 }}>Devices</span>
            <span style={{ color: 'var(--text-dim)' }}>
              {invite.sessionCount} signed in
              {invite.lastSeenAt ? ` · last seen ${formatDateTime(invite.lastSeenAt)}` : ''}
            </span>
          </div>
          {invite.note && (
            <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--text-faint)', minWidth: 92 }}>Note</span>
              <span style={{ color: 'var(--text-dim)' }}>{invite.note}</span>
            </div>
          )}
        </dl>

        <div className="divider" />
        <div className="chips">
          <button type="button" className="chip" onClick={onEdit}>
            <Icon name="menu" size={15} />
            Edit permissions
          </button>
          {status === 'revoked' ? (
            <button
              type="button"
              className="chip"
              disabled={busy}
              onClick={() => void run(() => adminApi.unrevoke(invite.id))}
            >
              <Icon name="check" size={15} />
              Restore
            </button>
          ) : (
            <button
              type="button"
              className="chip"
              disabled={busy}
              onClick={() => void run(() => adminApi.revoke(invite.id))}
            >
              <Icon name="close" size={15} />
              Revoke now
            </button>
          )}
          <button
            type="button"
            className="chip"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(`Delete ${invite.guestName}'s invite permanently?`)) return;
              void run(async () => {
                await adminApi.remove(invite.id);
                onBack();
              });
            }}
          >
            Delete
          </button>
        </div>
      </Panel>

      <AuditPanel entries={audit} title="This guest's activity" />
    </div>
  );
}

function AuditPanel({ entries, title }: { entries: AuditEntry[]; title: string }): ReactNode {
  return (
    <Panel title={title} icon="clock" className="stack">
      {entries.length === 0 ? (
        <div className="empty" style={{ padding: 'var(--space-5) 0' }}>
          Nothing yet.
        </div>
      ) : (
        <div className="audit-table">
          {entries.map((e) => (
            <div className="audit-row" key={e.id} data-denied={!e.allowed}>
              <span className="t">{formatDateTime(e.at)}</span>
              <span className="d">
                {e.kind}
                {e.entityId ? ` · ${e.entityId}` : ''}
                {e.detail ? ` · ${e.detail}` : ''}
              </span>
              <span className="t">{e.guestName ?? '—'}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function AdminApp(): ReactNode {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [house, setHouse] = useState<House | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [detailAudit, setDetailAudit] = useState<AuditEntry[]>([]);
  const [view, setView] = useState<View>({ name: 'list' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Flatten the whole document while the admin is mounted: the guest app's
  // translucent surfaces are wrong for dense forms and long lists.
  useEffect(() => {
    document.documentElement.dataset['surface'] = 'admin';
    return () => {
      delete document.documentElement.dataset['surface'];
    };
  }, []);

  const refresh = useCallback(async () => {
    const [h, i] = await Promise.all([adminApi.house(), adminApi.invites()]);
    setHouse(h.house);
    setInvites(i.invites);
  }, []);

  useEffect(() => {
    void adminApi
      .session()
      .then(async (s) => {
        setSignedIn(s.signedIn);
        if (s.signedIn) await refresh();
      })
      .catch(() => setSignedIn(false));
  }, [refresh]);

  useEffect(() => {
    if (view.name === 'audit') void adminApi.audit().then((a) => setAudit(a.entries));
    if (view.name === 'detail') void adminApi.invite(view.id).then((r) => setDetailAudit(r.audit));
  }, [view]);

  if (signedIn === null) {
    return <div className="admin-root empty">Loading…</div>;
  }

  if (!signedIn) {
    return (
      <div className="admin-root">
        <Login
          onIn={() => {
            setSignedIn(true);
            void refresh();
          }}
        />
      </div>
    );
  }

  const selected = view.name === 'detail' || view.name === 'edit'
    ? invites.find((i) => i.id === view.id)
    : undefined;

  const save = async (value: EditorValue, id?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = id ? await adminApi.update(id, value) : await adminApi.create(value);
      await refresh();
      setView({ name: 'detail', id: result.invite.id });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-root">
      <main className="shell shell-wide">
        {view.name === 'list' && (
          <>
            <div className="admin-head">
              <span className="hero-glyph">
                <Icon name="key" size={20} />
              </span>
              <h1>Guest access</h1>
              <span className="spacer" />
              <button type="button" className="icon-btn" onClick={() => setView({ name: 'info' })} aria-label="House notes">
                <Icon name="info" size={17} />
              </button>
              <button type="button" className="icon-btn" onClick={() => setView({ name: 'audit' })} aria-label="Activity log">
                <Icon name="clock" size={17} />
              </button>
              <button
                type="button"
                className="icon-btn"
                onClick={() => void adminApi.logout().then(() => setSignedIn(false))}
                aria-label="Sign out"
              >
                <Icon name="power" size={17} />
              </button>
            </div>

            <button
              type="button"
              className="btn btn-primary btn-block"
              style={{ marginBottom: 'var(--space-5)' }}
              onClick={() => setView({ name: 'new' })}
            >
              <Icon name="plus" size={17} />
              New guest invite
            </button>

            <div className="invite-list">
              {invites.length === 0 && <div className="empty">No invites yet.</div>}
              {invites.map((invite) => (
                <InviteRow
                  key={invite.id}
                  invite={invite}
                  onOpen={() => setView({ name: 'detail', id: invite.id })}
                />
              ))}
            </div>
          </>
        )}

        {view.name === 'new' && house && (
          <>
            <div className="admin-head">
              <button type="button" className="icon-btn" onClick={() => setView({ name: 'list' })} aria-label="Back">
                <Icon name="back" size={17} />
              </button>
              <h1>New invite</h1>
            </div>
            <InviteEditor
              house={house}
              busy={busy}
              onCancel={() => setView({ name: 'list' })}
              onSubmit={(v) => void save(v)}
            />
          </>
        )}

        {view.name === 'edit' && house && selected && (
          <>
            <div className="admin-head">
              <button
                type="button"
                className="icon-btn"
                onClick={() => setView({ name: 'detail', id: selected.id })}
                aria-label="Back"
              >
                <Icon name="back" size={17} />
              </button>
              <h1>Edit {selected.guestName}</h1>
            </div>
            <InviteEditor
              house={house}
              initial={selected}
              busy={busy}
              onCancel={() => setView({ name: 'detail', id: selected.id })}
              onSubmit={(v) => void save(v, selected.id)}
            />
          </>
        )}

        {view.name === 'detail' && house && selected && (
          <InviteDetail
            invite={selected}
            house={house}
            audit={detailAudit}
            onChanged={() => void refresh()}
            onEdit={() => setView({ name: 'edit', id: selected.id })}
            onBack={() => setView({ name: 'list' })}
          />
        )}

        {view.name === 'info' && <HouseInfoEditor onBack={() => setView({ name: 'list' })} />}

        {view.name === 'audit' && (
          <>
            <div className="admin-head">
              <button type="button" className="icon-btn" onClick={() => setView({ name: 'list' })} aria-label="Back">
                <Icon name="back" size={17} />
              </button>
              <h1>Activity</h1>
            </div>
            <AuditPanel entries={audit} title="Everything, newest first" />
          </>
        )}
      </main>
      {error && <Toast message={error} onDismiss={() => setError(null)} />}
    </div>
  );
}
